import { TFunction } from 'i18next'
import { TimelineProps } from '@node-space/ui-components'
import { Card, CardAudit, CardStatus, LimitChangeMetadata } from 'services/CardService'

type TimelineItem = TimelineProps['items'][number]
type TimelineVariant = TimelineItem['variant']

const getStatusVariant = (status: CardStatus): TimelineVariant => {
  switch (status) {
    case 'ACTIVE':
    case 'CREATED':
      return 'success'
    case 'BLOCKED':
    case 'CANCELLED':
    case 'EXPIRED':
      return 'error'
    case 'FROZEN':
    case 'PENDING':
      return 'warning'
    default:
      return 'info'
  }
}

const STATUS_TITLE_KEYS: Partial<Record<CardStatus, string>> = {
  CREATED: 'customers.cardActivity.issued',
  ACTIVE: 'customers.cardActivity.activated',
  FROZEN: 'customers.cardActivity.frozen',
  BLOCKED: 'customers.cardActivity.blocked',
  CANCELLED: 'customers.cardActivity.cancelled',
  PENDING: 'customers.cardActivity.pending',
  EXPIRED: 'customers.cardActivity.expired',
}

const appendActor = (title: string, actorName: string, t: TFunction): string => {
  return actorName.trim()
    ? `${title} ${t('customers.cardActivity.by', { actor: actorName })}`
    : title
}

const getStatusTitle = (status: CardStatus, actorName: string, t: TFunction): string => {
  const titleKey = STATUS_TITLE_KEYS[status] ?? 'customers.cardActivity.expired'
  const title = t(titleKey)
  if (status === 'EXPIRED') {
    return title
  }
  return appendActor(title, actorName, t)
}

const buildLimitTitle = (
  metadata: LimitChangeMetadata,
  actorName: string,
  t: TFunction
): { title: string; description?: string } => {
  const title = appendActor(t('customers.cardActivity.limitUpdated'), actorName, t)
  const newLimit = metadata.newLimit
  const description = newLimit
    ? t('customers.cardActivity.limitValue', {
        type: newLimit.type,
        value: newLimit.value,
      })
    : undefined
  return { title, description }
}

const buildItem = (audit: CardAudit, t: TFunction): TimelineItem | null => {
  const date = audit.createdAt ? new Date(audit.createdAt) : undefined
  const actorName = audit.actorName ?? ''

  if (audit.metadata?.type === 'STATUS_CHANGE') {
    if (audit.metadata.newStatus === 'CREATED') {
      return null
    }
    return {
      id: audit.id,
      date,
      variant: getStatusVariant(audit.metadata.newStatus),
      Title: getStatusTitle(audit.metadata.newStatus, actorName, t),
      description: `${t('customers.cardActivity.reason')}: ${audit.reason}`,
    }
  }

  if (audit.metadata?.type === 'LIMIT_CHANGE') {
    const { title, description } = buildLimitTitle(audit.metadata, actorName, t)
    const descriptions = [description, audit.reason].filter((entry): entry is string =>
      Boolean(entry)
    )
    return {
      id: audit.id,
      date,
      variant: 'info',
      Title: title,
      ...(descriptions.length > 0 && { description: descriptions }),
    }
  }

  return null
}

const buildIssuedItem = (card: Card, t: TFunction): TimelineItem => ({
  id: `card-issued-${card.id}`,
  date: card.createdAt ? new Date(card.createdAt) : undefined,
  variant: 'success',
  Title: t('customers.cardActivity.issuedWithCardNumber', { last4: card.last4Digits }),
})

export const getCardActivityItems = (card: Card, t: TFunction): TimelineProps['items'] => {
  const audit = card.audit ?? []

  const auditItems = audit
    .map(entry => buildItem(entry, t))
    .filter((item): item is TimelineItem => item !== null)

  return [...auditItems, buildIssuedItem(card, t)]
}
